import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

/**
 * Manual score-vs-audio alignment control for the Audio Track panel.
 *
 * The generated sync points can be slightly off (a small error in the detected
 * audio start, or encoder latency in the file), leaving the sheet music running
 * ahead of or behind the audio track. The − / + buttons nudge the alignment in
 * opposite directions so a conductor can dial it in by ear against the
 * metronome; Save persists the value for the slice.
 *
 * Adjustments apply live (each click fires `latency-change`); nothing is stored
 * until Save (`latency-save`).
 *
 * @fires audio-sync-change - detail: { offsetMs: number } on each −/+ click
 * @fires audio-sync-save - detail: { offsetMs: number } when Save is pressed
 */
@customElement('audio-sync-control')
export class AudioSyncControl extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
            }

            :host([hidden]) {
                display: none;
            }

            .label {
                font-size: 0.8rem;
                color: var(--text-secondary, #cbd5e1);
                white-space: nowrap;
            }

            .stepper {
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
            }

            button {
                background: #333;
                border: none;
                border-radius: 0.35rem;
                color: white;
                font-size: 0.9rem;
                line-height: 1;
                padding: 0.35rem 0.6rem;
                cursor: pointer;
                transition: background 0.2s;
            }

            button:hover:not([disabled]) {
                background: #444;
            }

            button[disabled] {
                opacity: 0.5;
                cursor: not-allowed;
            }

            .value {
                font-family: monospace;
                font-size: 0.85rem;
                color: white;
                min-width: 4.5rem;
                text-align: center;
                user-select: none;
                -webkit-user-select: none;
            }

            .save-btn {
                background: var(--primary-color, #2563eb);
            }

            .save-btn:hover:not([disabled]) {
                background: var(--primary-dark, #1e40af);
            }
        `,
    ];

    /** Current offset in ms. Positive shifts the sheet music later vs. the audio. */
    @property({ type: Number })
    offsetMs = 0;

    /** Step per button press. */
    @property({ type: Number })
    step = 50;

    /** Clamp — matches the server's accepted range. */
    @property({ type: Number })
    max = 5000;

    @property({ type: Boolean, reflect: true })
    disabled = false;

    /** True when the live value differs from what was last saved. */
    @property({ type: Boolean })
    dirty = false;

    private _nudge(delta: number): void {
        if (this.disabled) return;
        const next = Math.max(-this.max, Math.min(this.max, this.offsetMs + delta));
        if (next === this.offsetMs) return;
        this.offsetMs = next;
        this.dirty = true;
        this.dispatchEvent(
            new CustomEvent('audio-sync-change', {
                bubbles: true,
                composed: true,
                detail: { offsetMs: this.offsetMs },
            })
        );
    }

    private _save(): void {
        if (this.disabled || !this.dirty) return;
        this.dispatchEvent(
            new CustomEvent('audio-sync-save', {
                bubbles: true,
                composed: true,
                detail: { offsetMs: this.offsetMs },
            })
        );
    }

    render() {
        const sign = this.offsetMs > 0 ? '+' : '';
        return html`
            <span class="label" title="Shift the sheet music earlier or later relative to the audio track">
                Sync
            </span>
            <div class="stepper">
                <button
                    ?disabled=${this.disabled}
                    @click=${() => this._nudge(-this.step)}
                    title="Shift sheet music earlier by ${this.step}ms"
                    aria-label="Shift sheet music earlier"
                >${'−'}</button>
                <span class="value">${sign}${this.offsetMs} ms</span>
                <button
                    ?disabled=${this.disabled}
                    @click=${() => this._nudge(this.step)}
                    title="Shift sheet music later by ${this.step}ms"
                    aria-label="Shift sheet music later"
                >${'+'}</button>
            </div>
            <button
                class="save-btn"
                ?disabled=${this.disabled || !this.dirty}
                @click=${this._save}
                title="Save this offset for the slice"
            >Save</button>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'audio-sync-control': AudioSyncControl;
    }
}
